import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Center,
  Spinner,
  Text,
  SimpleGrid,
  HStack,
} from '@chakra-ui/react';
import {
  HiChevronDown,
  HiChevronRight,
  HiDocumentText,
  HiMapPin,
} from 'react-icons/hi2';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import { FormProvider } from 'react-hook-form';
import PlanTemplateForm from '../forms/PlanTemplateForm';
import {
  usePlanTemplate,
  useGeneratePlan,
  useActivatePlan,
  useUpdateApplicableSite,
} from '../../hooks/queries/useReunification';
import { ConcretePlanDetail } from './ConcretePlanDetail';
import {
  StationMappingsDrawer,
  ZoneMappingsDrawer,
  StaffAssignmentsDrawer,
} from './ConcretePlanDrawers';
import { getSiteData } from '../../types/ReunificationTypes';
import type {
  PlanApplicableSiteWithPlan,
  StationMapping,
  ZoneMapping,
  ConcreteStaffAssignment,
} from '../../types/ReunificationTypes';
import { toaster } from '../ui/toaster';
import {
  usePlansCrud,
  transformPlans,
  type PlanRow,
} from './hooks/usePlansCrud';
import { AddPlanDialog } from './AddPlanDialog';
import { useTranslation } from 'react-i18next';

function ApplicableSiteRow({
  site,
  onSelect,
}: {
  site: PlanApplicableSiteWithPlan;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const isReady = site.is_ready ?? false;
  const readinessScore = site.readiness_score ?? 0;

  return (
    <HStack
      px={4}
      py={3}
      bg="bg.subtle"
      borderRadius="md"
      justify="space-between"
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect();
      }}
      _hover={{ bg: 'bg.muted' }}
      data-e2e={`plan-site-row-${site.public_id}`}
    >
      <HStack gap={3}>
        <Box color="text.secondary" flexShrink={0}>
          <HiMapPin size={14} />
        </Box>
        <Text fontWeight="500" fontSize="sm">
          {site.site_name}
        </Text>
        <StatusBadge
          status={isReady ? 'active' : 'pending'}
          label={
            isReady
              ? t('common.ready', 'Ready')
              : t('common.pending', 'Pending')
          }
        />
        <Text fontSize="xs" color="text.secondary">
          {t('reunification.readiness', 'Readiness')}: {readinessScore}%
        </Text>
      </HStack>
      <Box p={0.5}>
        <HiChevronRight size={14} />
      </Box>
    </HStack>
  );
}

type EditDrawerType = 'stations' | 'zones' | 'staff' | null;

function ExpandedPlanContent({
  templateId,
  onEmpty,
}: {
  templateId: string;
  onEmpty?: () => void;
}) {
  const { data: detail, isLoading } = usePlanTemplate(templateId);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [editDrawer, setEditDrawer] = useState<EditDrawerType>(null);

  // Derive selectedSite from query data so it stays fresh after refetch
  const selectedSite = useMemo(
    () =>
      detail?.applicable_sites?.find((s) => s.public_id === selectedSiteId) ??
      null,
    [detail?.applicable_sites, selectedSiteId],
  );

  const activateMutation = useActivatePlan(templateId, selectedSiteId ?? '');
  const generateMutation = useGeneratePlan(templateId, selectedSiteId ?? '');
  const updateSiteMutation = useUpdateApplicableSite(templateId);

  const handleActivate = () => {
    activateMutation.mutate(0, {
      onSuccess: () => {
        toaster.create({ title: 'Plan activated', type: 'success' });
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to activate plan',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleSaveStations = (mappings: StationMapping[]) => {
    if (!selectedSite) return;
    const existing = selectedSite.data ?? {};
    updateSiteMutation.mutate(
      {
        siteId: selectedSite.public_id,
        data: { data: { ...existing, station_mappings: mappings } },
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Station mappings saved', type: 'success' });
          setEditDrawer(null);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to save',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleSaveZones = (mappings: ZoneMapping[]) => {
    if (!selectedSite) return;
    const existing = selectedSite.data ?? {};
    updateSiteMutation.mutate(
      {
        siteId: selectedSite.public_id,
        data: { data: { ...existing, zone_mappings: mappings } },
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Zone mappings saved', type: 'success' });
          setEditDrawer(null);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to save',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleSaveStaff = (assignments: ConcreteStaffAssignment[]) => {
    if (!selectedSite) return;
    const existing = selectedSite.data ?? {};
    updateSiteMutation.mutate(
      {
        siteId: selectedSite.public_id,
        data: { data: { ...existing, staff_assignments: assignments } },
      },
      {
        onSuccess: () => {
          toaster.create({
            title: 'Staff assignments saved',
            type: 'success',
          });
          setEditDrawer(null);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to save',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const sites = detail?.applicable_sites ?? [];
  const floorplans = detail?.floorplans ?? [];
  const isEmpty = !isLoading && sites.length === 0;

  const siteData = selectedSite ? getSiteData(selectedSite) : null;

  // Feature IDs assigned in station mappings (for zone drawer cross-type warnings)
  const stationFeatureIds = useMemo(
    () =>
      new Set(
        (siteData?.station_mappings ?? [])
          .map((m) => m.room_public_id)
          .filter(Boolean),
      ),
    [siteData?.station_mappings],
  );

  // Feature IDs assigned in zone mappings (for station drawer cross-type warnings)
  const zoneFeatureIds = useMemo(
    () =>
      new Set(
        (siteData?.zone_mappings ?? [])
          .map((m) => m.room_public_id)
          .filter(Boolean),
      ),
    [siteData?.zone_mappings],
  );

  useEffect(() => {
    if (isEmpty) onEmpty?.();
  }, [isEmpty, onEmpty]);

  if (isLoading) {
    return (
      <Box px={8} py={4}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (isEmpty) {
    return null;
  }

  return (
    <Box
      ml={6}
      pl={4}
      pr={6}
      py={3}
      display="flex"
      flexDirection="column"
      gap={2}
      borderLeftWidth={2}
      borderColor="border.primary"
    >
      {sites.map((site) => (
        <ApplicableSiteRow
          key={site.public_id}
          site={site}
          onSelect={() => setSelectedSiteId(site.public_id)}
        />
      ))}
      <DrawerComponent
        isOpen={!!selectedSiteId}
        onClose={() => setSelectedSiteId(null)}
        title={selectedSite?.site_name ?? 'Site Details'}
        e2eTitle="site-detail-drawer"
        size="lg"
        closeOnOutside
        closeOnEsc
      >
        {selectedSite && (
          <ConcretePlanDetail
            templateId={templateId}
            site={selectedSite}
            floorplans={floorplans}
            onActivate={handleActivate}
            isActivating={activateMutation.isPending}
            onGenerate={() => generateMutation.mutate()}
            isGenerating={generateMutation.isPending}
            onEditStations={() => setEditDrawer('stations')}
            onEditZones={() => setEditDrawer('zones')}
            onEditStaff={() => setEditDrawer('staff')}
          />
        )}
      </DrawerComponent>

      {selectedSite && siteData && (
        <>
          <StationMappingsDrawer
            isOpen={editDrawer === 'stations'}
            onClose={() => setEditDrawer(null)}
            mappings={siteData.station_mappings}
            floorplans={floorplans}
            siteId={selectedSite.site}
            onSave={handleSaveStations}
            isSaving={updateSiteMutation.isPending}
            assignedFeatureIds={zoneFeatureIds}
            crossTypeLabel="zones"
          />
          <ZoneMappingsDrawer
            isOpen={editDrawer === 'zones'}
            onClose={() => setEditDrawer(null)}
            mappings={siteData.zone_mappings}
            floorplans={floorplans}
            siteId={selectedSite.site}
            onSave={handleSaveZones}
            isSaving={updateSiteMutation.isPending}
            assignedFeatureIds={stationFeatureIds}
            crossTypeLabel="stations"
          />
          <StaffAssignmentsDrawer
            isOpen={editDrawer === 'staff'}
            onClose={() => setEditDrawer(null)}
            assignments={siteData.staff_assignments}
            floorplans={floorplans}
            onSave={handleSaveStaff}
            isSaving={updateSiteMutation.isPending}
          />
        </>
      )}
    </Box>
  );
}

export function PlansList() {
  const {
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,
    expandedRows,
    emptySites,
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleEmptyTemplate,
    toggleExpand,
    plans,
    totalItems,
    totalPages,
    isLoading,
    isFetching,
    error,
    drawerOpen,
    actionMode,
    currentRow,
    handleDrawerClose,
    openEditDrawer,
    openAddDrawer,
    methods,
    handleSubmit,
    handleDelete,
  } = usePlansCrud();

  const { t } = useTranslation();
  const [addPickerOpen, setAddPickerOpen] = useState(false);

  // AddPlanDialog (picker between "Clone ILUG" and "Start blank")
  // is shown when the user clicks the Create Plan primary action. It then
  // either opens the existing edit drawer on a freshly-cloned ILUG template
  // or falls through to openAddDrawer() for the blank-template path.
  const toggleOptions: ToggleOption[] = useMemo(
    () => [
      {
        value: 'all',
        label: t('common.all', 'All'),
        'data-e2e': 'plan-filter-all',
      },
      {
        value: 'published',
        label: t('reunification.published', 'Published'),
        'data-e2e': 'plan-filter-published',
      },
      {
        value: 'draft',
        label: t('reunification.draft', 'Draft'),
        'data-e2e': 'plan-filter-draft',
      },
    ],
    [t],
  );

  const columns: Column<PlanRow>[] = useMemo(
    () => [
      {
        key: 'expand',
        label: '',
        sortable: false,
        width: '40px',
        render: (row) =>
          row.sites > 0 && !emptySites.has(row.id) ? (
            <Box
              cursor="pointer"
              onClick={(e) => toggleExpand(row.id, e)}
              p={1}
            >
              {expandedRows.has(row.id) ? (
                <HiChevronDown size={16} />
              ) : (
                <HiChevronRight size={16} />
              )}
            </Box>
          ) : null,
      },
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: true,
        filterable: true,
        width: '240px',
        render: (row) => (
          <HStack gap={2}>
            <Box color="text.secondary" flexShrink={0}>
              <HiDocumentText size={14} />
            </Box>
            <Text fontSize="sm" color="text.primary">
              {row.name}
            </Text>
          </HStack>
        ),
      },
      {
        key: 'incident_type',
        label: t('reunification.incidentType', 'Incident Type'),
        sortable: true,
        filterable: true,
        width: '180px',
      },
      {
        key: 'reunification_mode',
        label: t('reunification.mode', 'Mode'),
        sortable: true,
        filterable: true,
        width: '120px',
      },
      {
        key: 'scale',
        label: t('reunification.scale', 'Scale'),
        sortable: true,
        filterable: true,
        width: '140px',
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('reunification.published', 'Published'), value: 'active' },
          { label: t('reunification.draft', 'Draft'), value: 'pending' },
        ],
        width: '120px',
        render: (row) => (
          <StatusBadge
            status={row.status}
            label={
              row.status === 'active'
                ? t('reunification.published', 'Published')
                : t('reunification.draft', 'Draft')
            }
          />
        ),
      },
      {
        key: 'sites',
        label: t('nav.sites', 'Sites'),
        sortable: false,
        width: '80px',
      },
      {
        key: 'version',
        label: t('reunification.version', 'Version'),
        sortable: false,
        width: '80px',
      },
    ],
    [t, emptySites, expandedRows, toggleExpand],
  );

  return (
    <>
      <TableOptions
        toggleOptions={toggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v);
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
        primaryAction={{
          label: t('reunification.createPlan', 'Create Plan'),
          // Open the AddPlanDialog picker instead of jumping
          // directly to the blank PlanTemplateForm. The picker offers
          // the canonical ILUG SRM template as the recommended path.
          onClick: () => setAddPickerOpen(true),
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : t('reunification.failedLoadPlans', 'Failed to load plans')}
            </Text>
          </Center>
        ) : plans.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">
                {t('reunification.noPlansFound', 'No plans found')}
              </Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={plans}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={openEditDrawer}
              renderExpandedRow={(row) =>
                expandedRows.has(row.id) ? (
                  <ExpandedPlanContent
                    templateId={row.id}
                    onEmpty={() => handleEmptyTemplate(row.id)}
                  />
                ) : null
              }
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        <DrawerComponent
          isOpen={drawerOpen}
          onClose={handleDrawerClose}
          title={
            actionMode === 'add'
              ? t('reunification.createPlanTitle', 'Create Reunification Plan')
              : t('reunification.editPlan', 'Edit Plan')
          }
          e2eTitle="plan-edit-drawer"
          size="lg"
          closeOnOutside={false}
          closeOnEsc={false}
          footer={
            <SimpleGrid
              columns={actionMode === 'edit' ? 3 : 2}
              gap={4}
              width="100%"
            >
              {actionMode === 'edit' && (
                <ButtonComponent
                  visual="alert"
                  type="button"
                  onClick={handleDelete}
                  data-e2e="delete-plan-button"
                >
                  {t('common.delete', 'Delete')}
                </ButtonComponent>
              )}
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={handleDrawerClose}
                data-e2e="cancel-plan-button"
              >
                {t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                type="submit"
                form="plan-template-form"
                data-e2e="save-plan-button"
              >
                {t('common.save', 'Save')}
              </ButtonComponent>
            </SimpleGrid>
          }
        >
          <FormProvider {...methods}>
            <form
              id="plan-template-form"
              onSubmit={methods.handleSubmit(handleSubmit)}
            >
              <PlanTemplateForm templateId={currentRow?.id} />
            </form>
          </FormProvider>
        </DrawerComponent>
      </Box>

      {/* Create-plan picker. Clone path opens edit drawer on the
          new clone; blank path falls through to the legacy add drawer. */}
      <AddPlanDialog
        isOpen={addPickerOpen}
        onClose={() => setAddPickerOpen(false)}
        onBlankSelected={openAddDrawer}
        onCloneSuccess={(clone) => {
          const [row] = transformPlans([clone]);
          openEditDrawer(row);
        }}
      />
    </>
  );
}
